import React, { useState, useEffect, useMemo } from "react"
import { Accordion, Button, Card, IconButton } from 'components/common';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getBuildingTypeLevel1, getContactModes, getLeadSources, getIndustries } from "redux/crm/settingSlice";
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select'
import { CRM_LEAD_RATING, CRM_LEGAL_STATUS, CRM_NUMBER_OF_EMPLOYEES, CRM_TYPE_OF_CUSTOMER } from 'app_config';
import { makeEmployeeLoader } from 'helpers/debounce';
import EmployeeSelectField from 'components/common/select/EmployeeSelectField';
import { getCountries } from 'redux/settingsSlice';
import { FaArrowLeft, FaPlus } from 'react-icons/fa';
import { createLead, updateLead } from 'redux/crm/leadSlice';
import { toast } from 'react-toastify';
import ContactForm from './ContarctForm';
import GoogleMapAutoCompletePlaceDebounce from 'components/common/google-map-places/GoogleMapAutoCompletePlaceDebounce';
import PropTypes from 'prop-types';

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';

const RATING_OPTIONS = CRM_LEAD_RATING.map((option) => ({ value: option, label: option }))
const TYPE_OF_CUSTOMER_OPTIONS = CRM_TYPE_OF_CUSTOMER.map((option) => ({ value: option, label: option }))
const NUMBER_OF_EMPLOYEES_OPTIONS = CRM_NUMBER_OF_EMPLOYEES.map((option) => ({ value: option, label: option }))

const fieldRow = (label, children) => (
  <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
    <div className="tw-col-span-12 sm:tw-col-span-3 tw-text-sm tw-font-medium tw-text-slate-700 lg:tw-text-right">
      {label}
    </div>
    <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
      {children}
    </div>
  </div>
);

const LeadForm = ({lead, title}) => {
  const {control, register, handleSubmit, formState: {errors}, setValue, getValues } = useForm()
  const [activeKey, setActiveKey] = useState(["0", "1"])
  const {leadSourceData, contactModeData, industryData} = useSelector((state) => state.crm_setting)
  const {countryData} = useSelector((state) => state.settings)

  const newContact = {
    salutation: null,
    first_name: null,
    last_name: null,
    position: null,
    email: null,
    mobile: null
  }

  const [contacts, setContacts] = useState([newContact]);
  const [contactsDeleted, setContactsDeleted] = useState([]);
  const [address, setAddress] = useState(null);
  const [projectLocation] = useState(null);

  const {data: user} = useSelector(state => state.profile_user)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    dispatch(getBuildingTypeLevel1())
    dispatch(getLeadSources())
    dispatch(getContactModes())
    dispatch(getIndustries())

    if (countryData.length === 0) {
      dispatch(getCountries())
    }
    // mount-only: load lookup data once; countryData.length is only a "skip if cached" guard
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  useEffect(() => {
    setValue("type_of_company", TYPE_OF_CUSTOMER_OPTIONS[0]);
    setValue("no_of_employee", NUMBER_OF_EMPLOYEES_OPTIONS[0]);

    if(lead) {
      Object.entries(lead).forEach(([key, value]) => {
        setValue(key, value)
      })

      setContacts(lead?.contacts);
    }
  }, [setValue, lead])

  const onSubmit = async (data) => {
    if(contacts) {
      const updateContacts = contacts.map(item => ({...item, main_contact: user?.name, sales_unit_id: user?.sales_unit.id, user_id: user.id}))

      if (contactsDeleted) {
        const deletedContacts = contactsDeleted.map(item => ({ ...item, _destroy: "1" }))
        const contactData = [...updateContacts, ...deletedContacts]

        data = {...data, contacts_attributes: contactData}
      }
      else
      {
        data = {...data, contacts_attributes: updateContacts}
      }
    }

    try {
      let leadId = null

      if (address) {
        data = {
          ...data,
          address: address.description
        };
      }

      if (projectLocation) {
        data = {
          ...data,
          project_location: projectLocation.description,
          province_project: projectLocation.city_province,
          country_project: projectLocation.country
        };
      }

      if (lead) {
        leadId = lead.id
        dispatch(updateLead({leadId, data }))
      }
      else {
        const response = await dispatch(createLead(data))

        if (response.payload && response.payload?.data?.id) {
          leadId = response.payload.data.id;
        } else {
          toast.error(response.payload.error)
        }
      }

      if (leadId) {
        navigate(`/crm/leads/${leadId}`)
      }
    }
    catch(error) {
      console.error('Lead submit failed:', error)
      toast.error(error.message)

      return;
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey)
  }

  const leadSourceOptions = useMemo(() => leadSourceData.map((option) => ({ value: option.name, label: option.name })), [leadSourceData])
  const defaultLeadSourceOption = useMemo(() => (
    lead && lead.source ? { value: lead.source, label: lead.source } : leadSourceData[0]?.name
  ), [lead, leadSourceData])

  const contactModeOptions = useMemo(() => contactModeData.map((option) => ({ value: option.name, label: option.name })), [contactModeData])
  const defaultContactModeOption = useMemo(() => (
    lead && lead.contact_mode ? { value: lead.contact_mode, label: lead.contact_mode } : contactModeData[0]?.name
  ), [lead, contactModeData])

  const industryOptions = useMemo(() => industryData.map((option) => ({ value: option.name, label: option.name })), [industryData])
  const defaultIndustryOption = useMemo(() => (
    lead && lead.industry ? { value: lead.industry, label: lead.industry } : industryData[0]?.name
  ), [lead, industryData])

  const ratingOptions = RATING_OPTIONS
  const defaultRatingOption = () => (
    lead && lead.rating ? { value: lead.rating, label: lead.rating } : null
  );

  // Rich options (avatar + code + full name + email); option.value stays the
  // employee code, which is what `owner_by` stores.
  const ownerOptions = useMemo(() => makeEmployeeLoader(), [])

  const defaultOwnerOption = useMemo(() => (
    lead && lead.owner_by ? { value: lead.owner_by, label: lead.owner_by } : null
  ), [lead])

  const typeOfCustomerOptions = TYPE_OF_CUSTOMER_OPTIONS
  const defaultTypeOfCustomerOption = useMemo(() => (
    lead ? { value: lead.type_of_company, label: lead.type_of_company } : TYPE_OF_CUSTOMER_OPTIONS[0]
  ), [lead])

  const numberOfEmployeesOptions = NUMBER_OF_EMPLOYEES_OPTIONS
  const defaultNumberOfEmployeesOption = useMemo(() => (
    lead ? { value: lead.no_of_employee, label: lead.no_of_employee } : NUMBER_OF_EMPLOYEES_OPTIONS[0]
  ), [lead])

  const handleAddContact = () => {
    const updatedContacts = [...contacts, newContact];
    setContacts(updatedContacts)
  }

  const handleRemoveContact = (index) => {
    //store after deleted
    const deleteContacts = contacts.filter((item, i) => i !== index)
    setContacts(deleteContacts)

    //store deleted items
    const contactsDeleted = contacts.filter((item, i) => i === index)
    setContactsDeleted(contactsDeleted)
  }

  const handleUpdatedContact = (index, contact) => {
    const updatedContacts = [...contacts]

    updatedContacts[index] = contact;
    setContacts(updatedContacts);
  }

  const fieldError = (error) => (
    <span className="tw-text-red-600 tw-text-xs">
      {error && error.message}
    </span>
  );

  const accordionHeader = (text) => (
    <span className="tw-font-medium tw-text-slate-900">{text}</span>
  );

  return (
    <Card
      className="tw-text-sm"
      padding="none"
      header={(
        <div className="tw-flex tw-justify-between tw-items-center">
          <h3 className="tw-font-semibold tw-text-sm tw-text-slate-900 tw-mb-0">{title}</h3>
          <IconButton
            onClick={handleBack}
            variant="secondary"
            size="sm"
            icon={<FaArrowLeft size={12} />}
          />
        </div>
      )}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="tw-text-sm">
        <div className="tw-p-4 tw-pb-24">
          <div className="tw-mb-2">All leads must be collected by Markering or Bussiness Development.</div>

          <Accordion
            className="tw-mt-3"
            activeKeys={activeKey}
            onChange={handleAccordionSelect}
          >
            <Accordion.Item eventKey="0" header={accordionHeader('1. Lead information (*)')}>
              <div className="tw-p-4">
                {fieldRow('Lead name', (
                  <>
                    <input
                      type="text"
                      placeholder="Lead name"
                      className={INPUT_CLASSES}
                      {...register("name", {
                        required: "Lead name must be required."
                      })}
                    />
                    {fieldError(errors.name)}
                  </>
                ))}

                {fieldRow('Rating', (
                  <>
                    <Controller
                      control={control}
                      name="rating"
                      render={() => (
                        <Select
                          closeMenuOnSelect={true}
                          options={ratingOptions}
                          defaultValue={defaultRatingOption}
                          placeholder='Select...'
                          classNamePrefix="react-select"
                          onChange={selectedOption => setValue('rating', selectedOption.value, {shouldValidate: true})}
                        />
                      )}
                      rules={{
                        required: {
                          value: true,
                          message: 'Rating must be required'
                        }
                      }}
                    />
                    {fieldError(errors.rating)}
                  </>
                ))}

                {fieldRow('Owner', (
                  <>
                    {/* EmployeeSelectField, not a bare AsyncSelect: it shows avatar,
                        full name and email so an owner code is recognizable. It
                        renders its own error message. */}
                    <EmployeeSelectField
                      name="owner_by"
                      control={control}
                      loadOptions={ownerOptions}
                      required
                      requiredMessage="Owner must be required"
                      defaultValue={defaultOwnerOption}
                      placeholder="Select..."
                    />
                  </>
                ))}

                <div className="tw-mt-4">
                  {fieldRow('Source', (
                    <>
                      <Controller
                        control={control}
                        name="source"
                        render={() => (
                          <Select
                            closeMenuOnSelect={true}
                            options={leadSourceOptions}
                            defaultValue={defaultLeadSourceOption}
                            placeholder='Select...'
                            classNamePrefix="react-select"
                            onChange={selectedOption => setValue('source', selectedOption.value, {shouldValidate: true})}
                          />
                        )}
                        rules={{
                          required: {
                            value: true,
                            message: 'Lead source must be required'
                          }
                        }}
                      />
                      {fieldError(errors.source)}
                    </>
                  ))}
                </div>

                {fieldRow('How does customer know Pebsteel?', (
                  <>
                    <Controller
                      control={control}
                      name="contact_mode"
                      render={() => (
                        <Select
                          closeMenuOnSelect={true}
                          options={contactModeOptions}
                          defaultValue={defaultContactModeOption}
                          placeholder='Select...'
                          classNamePrefix="react-select"
                          onChange={selectedOption => setValue('contact_mode', selectedOption.value, {shouldValidate: true})}
                        />
                      )}
                      rules={{
                        required: {
                          value: true,
                          message: 'How does customer know Pebsteel must be required'
                        }
                      }}
                    />
                    {fieldError(errors.contact_mode)}
                  </>
                ))}
              </div>
            </Accordion.Item>

            <Accordion.Item eventKey="1" header={accordionHeader('2. Account (*)')}>
              <div className="tw-p-4">
                {fieldRow('Name', (
                  <>
                    <input
                      type="text"
                      placeholder="Account name"
                      className={INPUT_CLASSES}
                      {...register("account_name", {
                        required: "Account name must be required."
                      })}
                    />
                    {fieldError(errors.account_name)}
                  </>
                ))}

                <div className="tw-mt-3">
                  {fieldRow('Legal status', (
                    <div className="tw-flex tw-flex-wrap tw-gap-3">
                      {CRM_LEGAL_STATUS.map((status) => (
                        <label key={status} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-cursor-pointer tw-mb-0">
                          <input
                            type="radio"
                            value={status}
                            defaultChecked={status === 'Private'}
                            className="tw-cursor-pointer"
                            {...register("legal_status")}
                          />
                          {status}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>

                {fieldRow('Type of company', (
                  <Controller
                    control={control}
                    name="type_of_company"
                    render={() => (
                      <Select
                        closeMenuOnSelect={true}
                        options={typeOfCustomerOptions}
                        defaultValue={defaultTypeOfCustomerOption}
                        placeholder='Select...'
                        classNamePrefix="react-select"
                        onChange={selectedOption => setValue('type_of_company', selectedOption.value)}
                      />
                    )}
                  />
                ))}

                {fieldRow('No. of employees', (
                  <Controller
                    control={control}
                    name="no_of_employee"
                    render={() => (
                      <Select
                        closeMenuOnSelect={true}
                        options={numberOfEmployeesOptions}
                        defaultValue={defaultNumberOfEmployeesOption}
                        placeholder='Select...'
                        classNamePrefix="react-select"
                        onChange={selectedOption => setValue('no_of_employee', selectedOption.value)}
                      />
                    )}
                  />
                ))}

                {fieldRow('Industry', (
                  <>
                    <Controller
                      control={control}
                      name="industry"
                      render={() => (
                        <Select
                          closeMenuOnSelect={true}
                          options={industryOptions}
                          defaultValue={defaultIndustryOption}
                          placeholder='Select...'
                          classNamePrefix="react-select"
                          onChange={selectedOption => setValue('industry', selectedOption.value, {shouldValidate: true})}
                        />
                      )}
                      rules={{
                        required: {
                          value: true,
                          message: 'Industry must be required'
                        }
                      }}
                    />
                    {fieldError(errors.industry)}
                  </>
                ))}

                {fieldRow('Work phone', (
                  <input
                    type="text"
                    placeholder="Work phone"
                    className={INPUT_CLASSES}
                    {...register("work_phone")}
                  />
                ))}

                {fieldRow('Work wmail', (
                  <>
                    <input
                      type="email"
                      placeholder="example@company.com"
                      className={INPUT_CLASSES}
                      {...register('email', {
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                    />
                    {fieldError(errors.work_email)}
                  </>
                ))}

                {fieldRow('Address', (
                  <GoogleMapAutoCompletePlaceDebounce id="lead-address" setAddress={setAddress} />
                ))}
              </div>
            </Accordion.Item>

            <Accordion.Item eventKey="2" header={accordionHeader('3. Contacts')}>
              <div className="tw-p-4">
                <div
                  className="tw-flex tw-justify-end tw-items-center tw-text-blue-600 tw-cursor-pointer tw-text-sm"
                  onClick={handleAddContact}
                >
                  <span className="circle-dashed">
                    <FaPlus />
                  </span>
                  <span className="tw-ml-3">Add Contact</span>
                </div>

                { contacts && contacts.map((item, index) => (
                  <ContactForm
                    index={index}
                    key={index}
                    lead={lead}
                    contact={item}
                    control={control}
                    register={register}
                    setValue={setValue}
                    getValues={getValues}
                    errors={errors}
                    removeContact={() => handleRemoveContact(index)}
                    updatedContact={handleUpdatedContact}
                  />
                ))}
              </div>
            </Accordion.Item>

            <Accordion.Item eventKey="3" header={accordionHeader('4. Customer message')}>
              <div className="tw-p-4">
                <div className="tw-mb-4 tw-text-blue-600">Customer left a message.</div>

                {fieldRow('Message', (
                  <textarea
                    rows={10}
                    placeholder="Message"
                    className={INPUT_CLASSES}
                    {...register("message")}
                  />
                ))}
              </div>
            </Accordion.Item>
          </Accordion>
        </div>

        <div className="tw-bg-slate-50 tw-fixed tw-bottom-0 tw-inset-x-0 tw-z-10 tw-text-center tw-p-3">
          <Button size="sm" type="submit" variant="primary" className="tw-mr-2">{lead ? "Update" : "Create"} Lead</Button>
          <Button size="sm" variant="danger" onClick={handleBack}>Cancel</Button>
        </div>
      </form>
    </Card>
  )
};

LeadForm.propTypes = {
  lead: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired
}

export default LeadForm;
